// games/shared/i18n.ts -- typed i18n for the arcade games (drift, rally,
// heli, tag, charge3d). English is bundled statically as the per-key
// fallback; the other 23 live-tenant locales are lazy-imported on boot so
// they never bloat the initial chunk.
//
// Locale resolution priority (resolveLocale):
//   1. ?lang= query override (base tag match)
//   2. URL path prefix (/fi/drift/, same shape main.tsx uses for charge)
//   3. navigator.language (base tag match)
//   4. "en"
import { type ArcadeMsgKey, en } from "./locales/en";

export type { ArcadeMsgKey };
export type ArcadeDict = Record<ArcadeMsgKey, string>;

/** Languages of all live routemap tenants (verified vs shared/lib/i18n). */
export const ARCADE_LOCALES = [
  "cs",
  "da",
  "de",
  "el",
  "en",
  "es",
  "et",
  "fi",
  "fr",
  "hr",
  "hu",
  "it",
  "lt",
  "lv",
  "nl",
  "no",
  "pl",
  "pt",
  "ro",
  "ru",
  "sk",
  "sl",
  "sr",
  "sv",
] as const;
export type ArcadeLocale = (typeof ARCADE_LOCALES)[number];

export function isArcadeLocale(s: string): s is ArcadeLocale {
  return (ARCADE_LOCALES as readonly string[]).includes(s);
}

// Explicit loader map (not a template import) so each locale becomes its own
// lazily-fetched Vite chunk and typos fail at compile time.
const LOADERS: Record<Exclude<ArcadeLocale, "en">, () => Promise<{ dict: ArcadeDict }>> = {
  cs: () => import("./locales/cs"),
  da: () => import("./locales/da"),
  de: () => import("./locales/de"),
  el: () => import("./locales/el"),
  es: () => import("./locales/es"),
  et: () => import("./locales/et"),
  fi: () => import("./locales/fi"),
  fr: () => import("./locales/fr"),
  hr: () => import("./locales/hr"),
  hu: () => import("./locales/hu"),
  it: () => import("./locales/it"),
  lt: () => import("./locales/lt"),
  lv: () => import("./locales/lv"),
  nl: () => import("./locales/nl"),
  no: () => import("./locales/no"),
  pl: () => import("./locales/pl"),
  pt: () => import("./locales/pt"),
  ro: () => import("./locales/ro"),
  ru: () => import("./locales/ru"),
  sk: () => import("./locales/sk"),
  sl: () => import("./locales/sl"),
  sr: () => import("./locales/sr"),
  sv: () => import("./locales/sv"),
};

/** "fi-FI" -> "fi"; lowercased. */
function baseTag(tag: string): string {
  return tag.trim().toLowerCase().split("-")[0] ?? "";
}

export interface ResolveLocaleInput {
  /** location.search, e.g. "?lang=fi" */
  search?: string;
  /** location.pathname, e.g. "/fi/drift/" */
  path?: string;
  /** navigator.language, e.g. "de-AT" */
  navLang?: string;
  /** Server-injected tenant locale (window.__TRIPPI_LOCALE__). The arcade
   *  server stamps this into the served index.html per request Host when
   *  the games run same-origin under tenant.tld/trippi/ (no /<locale>/
   *  path segment is present there). Lower priority than an explicit
   *  ?lang= or path prefix so the URL always wins; higher than the
   *  browser's navigator.language so a Finnish tenant defaults to fi even
   *  for a visitor whose browser is set to English. */
  tenantLocale?: string;
}

/**
 * Pure locale resolution:
 *   ?lang= > path prefix > tenant-injected locale > navigator.language > en.
 */
export function resolveLocale(input: ResolveLocaleInput): ArcadeLocale {
  const lang = new URLSearchParams(input.search ?? "").get("lang");
  if (lang) {
    const base = baseTag(lang);
    if (isArcadeLocale(base)) return base;
  }
  const seg = (input.path ?? "").split("/").filter(Boolean)[0] ?? "";
  if (/^[a-z]{2}(-[a-z]{2})?$/i.test(seg)) {
    const base = baseTag(seg);
    if (isArcadeLocale(base)) return base;
  }
  if (input.tenantLocale) {
    const base = baseTag(input.tenantLocale);
    if (isArcadeLocale(base)) return base;
  }
  if (input.navLang) {
    const base = baseTag(input.navLang);
    if (isArcadeLocale(base)) return base;
  }
  return "en";
}

// Active dictionary; en until initGameI18n() swaps it. t() falls back to the
// bundled English value per key, so a missing key never renders blank.
let active: Partial<ArcadeDict> = en;
let activeLocale: ArcadeLocale = "en";

/**
 * Resolve (or accept) a locale and lazy-load its dictionary. Call once on
 * game boot, before the first HUD render. Network/chunk failure falls back
 * to English instead of blocking the game.
 */
export async function initGameI18n(locale?: ArcadeLocale): Promise<ArcadeLocale> {
  const loc = locale ?? resolveLocale({
    search: typeof location !== "undefined" ? location.search : "",
    path: typeof location !== "undefined" ? location.pathname : "",
    navLang: typeof navigator !== "undefined" ? navigator.language : "",
    tenantLocale: typeof globalThis !== "undefined"
      ? (globalThis as { __TRIPPI_LOCALE__?: string }).__TRIPPI_LOCALE__
      : undefined,
  });
  if (loc === "en") {
    active = en;
    activeLocale = "en";
    return "en";
  }
  try {
    active = (await LOADERS[loc]()).dict;
    activeLocale = loc;
  } catch {
    active = en;
    activeLocale = "en";
  }
  return activeLocale;
}

export function currentLocale(): ArcadeLocale {
  return activeLocale;
}

/** Test hook: install a (possibly partial) dictionary directly. */
export function setDictionaryForTest(locale: ArcadeLocale, dict: Partial<ArcadeDict>): void {
  active = dict;
  activeLocale = locale;
}

/** Translate a key with {placeholder} substitution; falls back to English. */
export function t(key: ArcadeMsgKey, params?: Record<string, string | number>): string {
  const raw = active[key] ?? en[key];
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const val = params[name];
    return val !== undefined ? String(val) : whole;
  });
}

/** Newline-separated list value (controls/tips) -> trimmed lines. */
export function tLines(key: ArcadeMsgKey): string[] {
  return t(key).split("\n").map((s) => s.trim()).filter((s) => s.length > 0);
}
